import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { GeckoFields, GECKO_UTILS, GECKO_CONSTS } from 'ui';
import { CONSTS } from 'constants/index';
import AgentTeamSelect from 'components/AgentTeamSelect';
import GeckoFeatures from 'services/gecko-features';
import FilePicker from 'components/FilePicker/FilePicker.jsx';
import BotSelect from 'components/BotSelect';
import './LiveChatChannelForm.scss';
import PreChatFormFieldSelector from 'components/PreChatFormFieldSelector';
import GeckoStore from 'services/gecko-store';

const TranslateBotMessages = (props) => {
  const { value = false, onChange = () => {} } = props;
  const fields = () => {
    return [
      {
        key: 'translate_bot_messages',
        type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
      },
    ];
  };
  const changeHandler = (_key, value) => {
    onChange(value);
  };
  return (
    <div className="TranslateBotMessages">
      <GeckoFields
        values={{ translate_bot_messages: value }}
        fields={fields()}
        handler={changeHandler}
      />
    </div>
  );
};
TranslateBotMessages.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func,
};

const AISuggestions = (props) => {
  const { value = false, onChange = () => {} } = props;
  const fields = () => {
    return [
      {
        key: 'ai_suggestions',
        type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
      },
    ];
  };
  const changeHandler = (_key, value) => {
    onChange(value);
  };
  return (
    <div className="AISuggestions">
      <GeckoFields
        values={{ ai_suggestions: value }}
        fields={fields()}
        handler={changeHandler}
      />
    </div>
  );
};
AISuggestions.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func,
};

const BlockOfflineMessagesToggle = (props) => {
  const { value = false, onChange = () => {} } = props;
  const fields = () => {
    return [
      {
        key: 'block_offline_messages',
        type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
      },
      {
        key: 'block_offline_message_warning',
        type: GECKO_CONSTS.FIELD_TYPE_DISPLAY,
        width: '100',
        hideWhen: () => !GeckoFeatures.can('chat-bot') || !value,
        render: () => {
          return (
            <div
              className="gecko-alert alert alert-warning"
              role="alert"
              aria-live="off"
            >
              Leave this off to allow Bots to be able to respond while your
              agents are unavailable
            </div>
          );
        },
      },
    ];
  };
  const changeHandler = (_key, value) => {
    onChange(value);
  };
  return (
    <div className="BlockOfflineMessagesToggle">
      <GeckoFields
        values={{ block_offline_messages: value }}
        fields={fields()}
        handler={changeHandler}
      />
    </div>
  );
};
BlockOfflineMessagesToggle.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func,
};

const websiteToOrigin = (websiteURL) => {
  const DEFAULT_SCHEME = 'https';
  const withScheme = /^https?:\/\//.test(websiteURL)
    ? websiteURL
    : `${DEFAULT_SCHEME}://${websiteURL}`;
  const withoutPath = withScheme.split('/').slice(0, 3).join('/');
  return withoutPath;
};

const canEnableAiSuggestions = () =>
  GeckoFeatures.can('chat-bot') && GeckoStore.redux.get('appState.isSupport');

class LiveChatChannelForm extends Component {
  static propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func,
  };
  static defaultProps = {
    onChange: () => {},
  };

  changeHandler(key, value) {
    if (key === 'configuration.allowedOrigins') {
      const allowedOrigins = value.reduce((allowedOrigins, website) => {
        const origin = websiteToOrigin(website);
        if (allowedOrigins.includes(origin)) {
          return allowedOrigins;
        }
        return [...allowedOrigins, origin];
      }, []);
      const newValue = GECKO_UTILS.setNestedValue(
        this.props.value,
        key,
        allowedOrigins
      );
      return this.props.onChange(newValue);
    }
    const newValue = GECKO_UTILS.setNestedValue(this.props.value, key, value);
    return this.props.onChange(newValue);
  }

  componentDidMount() {
    if (
      this.props.value.configuration.has_advanced_settings === undefined ||
      this.props.value.configuration.has_advanced_settings === null
    ) {
      const newConfiguration = Object.assign(
        {},
        this.props.value.configuration,
        { has_advanced_settings: false }
      );
      const newValue = Object.assign({}, this.props.value, {
        configuration: newConfiguration,
      });
      return this.props.onChange(newValue);
    }
    if (
      this.props.value.configuration.hide_unavailable === undefined ||
      this.props.value.configuration.hide_unavailable === null
    ) {
      const newConfiguration = Object.assign(
        {},
        this.props.value.configuration,
        { hide_unavailable: false }
      );
      const newValue = Object.assign({}, this.props.value, {
        configuration: newConfiguration,
      });
      return this.props.onChange(newValue);
    }
  }

  componentDidUpdate() {
    if (
      this.props.value.configuration.has_advanced_settings === undefined ||
      this.props.value.configuration.has_advanced_settings === null
    ) {
      const newConfiguration = Object.assign(
        {},
        this.props.value.configuration,
        { has_advanced_settings: false }
      );
      const newValue = Object.assign({}, this.props.value, {
        configuration: newConfiguration,
      });
      return this.props.onChange(newValue);
    }
    if (
      this.props.value.configuration.hide_unavailable === undefined ||
      this.props.value.configuration.hide_unavailable === null
    ) {
      const newConfiguration = Object.assign(
        {},
        this.props.value.configuration,
        { hide_unavailable: false }
      );
      const newValue = Object.assign({}, this.props.value, {
        configuration: newConfiguration,
      });
      return this.props.onChange(newValue);
    }
  }

  fields = [
    {
      label: 'Embed Code',
      description: (
        <p>
          Copy the code snippet below and paste it into the body of your website
          on any page you want the chat widget to appear. For more information,
          visit our{' '}
          <a
            href="https://academy.geckoengage.com/en/articles/6962551-channels"
            target="_blank"
          >
            academy
          </a>
        </p>
      ),
      key: 'configuration.random_key',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: 'col-xs-12',
      hideWhen: () => !this.props.value.configuration.random_key,
      component: (
        <code style={{ backgroundColor: '#f7f7f9' }}>
          <pre>
            <span>{'<'}</span>
            <span style={{ color: '#bd4147' }}>{'script'}</span>
            <span>{'>'}</span>
            <br />
            <span style={{ color: '#2795EE' }}>{'    window'}</span>
            <span>{'.GeckoChatSettings = {'}</span>
            <br />
            <span>{'        account_id: '}</span>
            <span
              style={{ color: '#249D7F' }}
            >{`'${this.props.value.configuration.random_key}'`}</span>
            <span>{','}</span>
            <br />
            <span>{'        auto_boot: '}</span>
            <span style={{ color: '#219' }}>{'true'}</span>
            <br />
            <span>{'    };'}</span>
            <br />
            <span>{'</'}</span>
            <span style={{ color: '#bd4147' }}>{'script'}</span>
            <span>{'>'}</span>
            <br />
            <span>{'<'}</span>
            <span style={{ color: '#bd4147' }}>{'script'}</span>
            <span style={{ color: '#f18c16' }}>{' src'}</span>
            <span>{'='}</span>
            <span style={{ color: '#249D7F' }}>
              {'"https://embed.geckochat.io"'}
            </span>
            <span>{'>'}</span>
            <span>{'</'}</span>
            <span style={{ color: '#bd4147' }}>{'script'}</span>
            <span>{'>'}</span>
          </pre>
        </code>
      ),
    },
    {
      key: 'name',
      type: GECKO_CONSTS.FIELD_TYPE_TEXT,
      label: 'Channel Title',
      width: 'col-xs-12',
      required: true,
    },
    {
      key: 'configuration.color',
      type: GECKO_CONSTS.FIELD_TYPE_COLOR,
      label: 'Widget Colour',
      class: 'col-xs-6',
      required: true,
    },
    {
      key: 'configuration.logo',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      class: 'col-xs-6',
      label: 'Widget Header Logo',
      description: '225px by 30px (recommended)',
      component: <FilePicker accept={CONSTS.IMAGE_ACCEPT_MIME_TYPES} />,
    },
    {
      label: 'Widget Design',
      description:
        'Choose how you want the chat widget bubble to be displayed on your website.',
      key: 'configuration.has_advanced_settings',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      width: 100,
      options: [
        { label: 'Default', value: false },
        { label: 'Customised', value: true },
      ],
    },
    {
      key: 'configuration.open_icon',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      description: '70px by 70px (recommended)',
      label: 'Open Icon',
      class: 'col-4',
      hideWhen: () => {
        return !this.props.value.configuration.has_advanced_settings;
      },
      component: <FilePicker accept={CONSTS.IMAGE_ACCEPT_MIME_TYPES} />,
      required: true,
    },
    {
      key: 'configuration.close_icon',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      description: '70px by 70px (recommended)',
      label: 'Close Icon',
      class: 'col-4',
      hideWhen: () => {
        return !this.props.value.configuration.has_advanced_settings;
      },
      component: <FilePicker accept={CONSTS.IMAGE_ACCEPT_MIME_TYPES} />,
      required: true,
    },
    {
      label: 'Chat Intro Message',
      description:
        'This message is displayed to the visitor before the conversation is started.',
      key: 'configuration.welcome_message',
      type: GECKO_CONSTS.FIELD_TYPE_TEXTAREA,
      width: 'col-xs-12',
    },
    {
      label: 'Unavailability Options',
      description: `Choose how the chat bubble behaves when no agents are available.`,
      key: 'configuration.hide_unavailable',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      width: 100,
      options: [
        { label: 'Show Unavailable message', value: false },
        { label: 'Hide widget', value: true },
      ],
    },
    {
      key: 'configuration.unavailable_message',
      type: GECKO_CONSTS.FIELD_TYPE_TEXTAREA,
      label: 'Unavailable Message',
      width: 'col-xs-12',
      hideWhen: () => {
        return !!this.props.value.configuration.hide_unavailable;
      },
    },
    {
      label: 'Disable incoming messages when unavailable?',
      description:
        "When toggled on, students won't be able to send messages when your agents are unavailable",
      key: 'configuration.block_offline_messages',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: 'col-xs-12',
      hideWhen: () => {
        return !!this.props.value.configuration.hide_unavailable;
      },
      component: <BlockOfflineMessagesToggle />,
    },
    {
      label: 'Available When',
      description:
        'Choose which users or teams need to be Available for this chat to be Available.',
      key: 'configuration.available_when',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: <AgentTeamSelect />,
      width: 'col-xs-12',
    },
    {
      label: 'Pre-Chat Form',
      description: (
        <span>
          Choose the information users will provide you with prior to initiating
          a chat. <span style={{ fontWeight: 'bold' }}>Please note:</span> if
          you have an active integration that requires certain fields to be
          synced, you may want to ensure these are on your form and set to
          required
        </span>
      ),
      key: 'configuration.pre_chat_form',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: (
        <PreChatFormFieldSelector customFields={this.props.customFields} />
      ),
      width: 'col-xs-12',
    },
    {
      label: 'Translate Bot Messages',
      description:
        'Enable students to communicate with their preferred language and have your bot answers translated automatically.',
      key: 'configuration.translate_bot_messages',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: 'col-xs-12',
      hideWhen: () => {
        return !GeckoFeatures.can('chat-bot');
      },
      component: <TranslateBotMessages />,
    },
    {
      label: 'Use bot suggestions',
      description:
        'Choose which bot would you like, to give you response suggestions in this channel',
      key: 'configuration.use_bot_for_suggestions',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: 'col-xs-12',
      hideWhen: () => {
        return (
          !GeckoFeatures.can('chat-bot') ||
          !GeckoFeatures.can('bot-suggestions')
        );
      },
      component: <BotSelect />,
    },
    {
      label: 'Enable AI Suggested Replies',
      isBeta: true,
      description:
        'Adds a new option to Live Chat conversations. Agents will be able to ask ChatGPT to generate a reply.',
      key: 'configuration.ai_suggestions',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: 'col-xs-12',
      hideWhen: () => !canEnableAiSuggestions(),
      component: <AISuggestions />,
    },
    {
      key: 'configuration.ai_site_id',
      label: 'Site ID',
      width: '33%',
      type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
      decimalPlaces: 0,
      hideWhen: () => {
        return (
          !canEnableAiSuggestions() ||
          !this.props.value.configuration.ai_suggestions
        );
      },
      required: true,
    },
    {
      key: 'configuration.ai_model',
      label: 'ChatGPT Model',
      width: '33%',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      options: GeckoFeatures.can('gpt4')
        ? [
            { label: 'GPT-3.5', value: 'gpt-3.5-turbo' },
            { label: 'GPT-4', value: 'gpt-4' },
          ]
        : [{ label: 'GPT-3.5', value: 'gpt-3.5-turbo' }],
      config: {
        clearable: false,
      },
      required: true,
      hideWhen: () => {
        return (
          !canEnableAiSuggestions() ||
          !this.props.value.configuration.ai_suggestions
        );
      },
    },
    {
      label: 'Allowed websites',
      placeholder: 'e.g. https://geckoengage.com',
      description:
        'Restrict your widget to only load on the following websites',
      key: 'configuration.allowedOrigins',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT_CREATE_MULTI,
      width: 'col-xs-12',
    },
  ];

  render() {
    return (
      <GeckoFields
        fields={this.fields}
        values={this.props.value}
        handler={this.changeHandler.bind(this)}
      />
    );
  }
}

export default LiveChatChannelForm;
